/**
 * SeeMe Dropdown System
 * Lightweight vanilla JS for dropdown menus.
 *
 * Usage — add data-dropdown to any trigger inside a .dropdown:
 *   <div class="dropdown">
 *       <button data-dropdown>Toggle</button>
 *       <div class="dropdown-menu">...</div>
 *   </div>
 *
 * The trigger toggles .show on the nearest .dropdown-menu sibling.
 * Closes on click outside, Escape key, or clicking a .dropdown-item.
 *
 * JS manages .show and .dropdown-menu--up, plus the [data-dropdown-xflip]
 * marker that pins a horizontal correction for as long as the menu stays open.
 * Phone sheets add .dropdown-sheet and .dropdown-sheet--picker (see Sheet Mode).
 * Other classes on the menu are never rewritten: dropdown-menu-form,
 * dropdown-menu-popout and friends stay read-only user configuration.
 *
 * Alignment is the one place JS may overrule the author, and only through inline
 * left/right — the class itself is left alone, so the authored alignment comes
 * back the moment it fits. A dropdown-menu-end menu anchored to a narrow trigger
 * near the left edge of a sheet runs leftward straight out of it, and a
 * start-aligned menu does the mirror image on the right; both are re-aligned
 * rather than left hanging outside. The boundary is whatever actually bounds the
 * menu, not the viewport, so this fires inside sheets and clipping cards that sit
 * nowhere near the edge of the screen.
 *
 * Portal Mode:
 * Popout menus (.dropdown-menu-popout) are portaled to document.body to escape
 * overflow:hidden constraints in table cells. A portaled menu points at its
 * .dropdown through menu._dropdownOwner, and the .dropdown points at the menu
 * through dropdown._portaledMenu, so typeahead.js and combobox.js can still
 * get from one to the other.
 *
 * Sheet Mode:
 * Below 768px every menu opens as a bottom sheet portaled to body. Two kinds
 * stay floating: .dropdown-menu-panel / -popout detail menus, and menus whose
 * trigger carries data-no-sheet. JS adds .dropdown-sheet when the markup lacks
 * it and leaves it on after close. It toggles .dropdown-sheet--picker on a
 * menu that holds a typeahead or combobox, recomputed on every open. Each open sheet
 * tracks visualViewport through two inline custom properties,
 * --sheet-keyboard-offset and --sheet-visible-height, so CSS can keep it
 * above the iOS keyboard.
 *
 * Lazy-loaded menus:
 * A menu whose content arrives after the click is placed at spinner size, so
 * both paths watch the open menu with a MutationObserver and re-place it once
 * the content lands — no framework is assumed. Re-placement is monotonic: a
 * menu may flip up but never flops back down. Portals additionally flip on a
 * buffered height at click time so the common case needs no correction, and
 * still listen for htmx:afterSettle to catch content that is animating in.
 */

(function () {
    // Track portaled menus - store original position info for restoration
    var portaledMenus = new Map(); // dropdown -> { menu, trigger, originalParent, originalNextSibling, sheet }

    // ── Backdrops ─────────────────────────────────────────────────────────────
    var sheetBackdrop = null;
    var dropdownBackdrop = null;
    var BP_SM = 768; // matches @bp-sm in variables.less

    function getBottomOffset(el) {
        return parseInt(getComputedStyle(el).getPropertyValue('--dropdown-bottom-offset')) || 0;
    }

    // ── In-place placement ────────────────────────────────────────────────────
    // Non-portaled menus stay in the DOM and are positioned by CSS; JS only
    // picks the direction (.dropdown-menu--up) and the right-align fallback.

    // Nearest ancestor that genuinely clips, as a rect. An in-place menu is an
    // ordinary absolutely-positioned descendant, so an overflow:hidden ancestor
    // bounds it long before the viewport does. Scrollable ancestors (auto /
    // scroll) are deliberately NOT treated as boundaries — there the overflow is
    // still reachable, and the two that do need escaping (.data-table,
    // .filter-bar) are routed to the portal path before we ever get here.
    // The walk stops AFTER a position:fixed ancestor: overflow above that point
    // no longer clips. Test overflow before position, so a container that is
    // both fixed and clipping (sheets, modals, mobile nav) still reports itself.
    //
    // `axis` selects which overflow property bounds the walk — 'y' (the default)
    // for the flip-up decision, 'x' for the alignment decision. The two axes are
    // asked separately on purpose: overflow-x and overflow-y routinely differ on
    // the same element, and CSS computes a `visible` axis to `auto` whenever its
    // partner is not `visible`, so one shared walk would answer each axis with
    // the other's boundary.
    function getClipRect(el, axis) {
        var prop = axis === 'x' ? 'overflowX' : 'overflowY';
        var node = el.parentElement;
        while (node && node !== document.body && node !== document.documentElement) {
            var cs = getComputedStyle(node);
            if (cs[prop] === 'hidden' || cs[prop] === 'clip') {
                return node.getBoundingClientRect();
            }
            if (cs.position === 'fixed') return null;
            node = node.parentElement;
        }
        return null;
    }

    // Idempotent, so it can be re-run while the menu is open — that is what
    // lets lazily-loaded content be re-placed once it actually lands.
    //
    // `initial` is true only for the click-time call. Later calls may move a
    // menu off its authored position but never back onto it: content arriving
    // must be able to rescue a menu that is now clipped, while a menu already
    // moved must not snap back when a form inside it swaps or shrinks. Same
    // reasoning as the portal path's monotonic settle re-position — so repeated
    // mutations can't oscillate.
    function placeInPlace(menu, trigger, initial) {
        // One lock per axis. Vertical pins with the class it owns; horizontal
        // pins with [data-dropdown-xflip], which the MutationObserver below
        // ignores (it watches childList/characterData only), so writing it
        // cannot retrigger the re-place that wrote it.
        var lockedUp = !initial && menu.classList.contains('dropdown-menu--up');
        var lockedEdge = !initial && menu.hasAttribute('data-dropdown-xflip');

        // Measure in the authored position, then decide. A locked axis keeps its
        // override — clearing it would move the menu back before we had a chance
        // to re-confirm the correction.
        if (!lockedUp) menu.classList.remove('dropdown-menu--up');
        if (!lockedEdge) {
            menu.style.right = '';
            menu.style.left = '';
        }

        var rect = menu.getBoundingClientRect();
        var triggerRect = trigger.getBoundingClientRect();

        // A sheet body is the visible band for ordinary in-place dropdowns on
        // desktop, on BOTH axes: its backdrop makes overflow below a pinned
        // footer, or beside a panel docked to one side, unreachable by
        // scrolling. It has to be named explicitly rather than discovered by
        // getClipRect — it scrolls (overflow-y: auto), and a scrollable ancestor
        // is deliberately not treated as a clip boundary.
        var sheetBody = window.innerWidth >= BP_SM && trigger.closest('.sheet__body');
        var sheetRect = sheetBody ? sheetBody.getBoundingClientRect() : null;

        if (!lockedUp) {
            var bottomOffset = getBottomOffset(trigger);
            var effectiveBottom = window.innerHeight - bottomOffset;
            var effectiveTop = 0;
            var clipY = getClipRect(trigger, 'y');
            if (clipY) {
                effectiveBottom = Math.min(effectiveBottom, clipY.bottom);
                effectiveTop = Math.max(effectiveTop, clipY.top);
            }
            if (sheetRect) {
                effectiveBottom = Math.min(effectiveBottom, sheetRect.bottom);
                effectiveTop = Math.max(effectiveTop, sheetRect.top);
            }

            // Flip up only when it actually helps — more room above the trigger
            // than below it, measured against the same boundaries.
            if (rect.bottom > effectiveBottom &&
                triggerRect.top - effectiveTop > effectiveBottom - triggerRect.bottom) {
                menu.classList.add('dropdown-menu--up');
            }
        }

        if (!lockedEdge) {
            // Same shape as the vertical decision, one axis over. The band is the
            // viewport narrowed by whatever actually bounds the menu, so a menu
            // overflowing a sheet or a clipping card is corrected even though it
            // is nowhere near the edge of the screen.
            var effectiveRight = window.innerWidth;
            var effectiveLeft = 0;
            var clipX = getClipRect(trigger, 'x');
            if (clipX) {
                effectiveRight = Math.min(effectiveRight, clipX.right);
                effectiveLeft = Math.max(effectiveLeft, clipX.left);
            }
            if (sheetRect) {
                effectiveRight = Math.min(effectiveRight, sheetRect.right);
                effectiveLeft = Math.max(effectiveLeft, sheetRect.left);
            }

            // Unlike the vertical flip, a menu overlaps its trigger
            // horizontally: a start-aligned menu runs rightward from the
            // trigger's LEFT edge, an end-aligned one runs leftward from its
            // RIGHT edge. So each alignment's room is measured from the edge it
            // anchors to, not from the far side of the trigger.
            var roomRight = effectiveRight - triggerRect.left;
            var roomLeft = triggerRect.right - effectiveLeft;
            var isEnd = menu.classList.contains('dropdown-menu-end') ||
                        menu.classList.contains('dropdown-menu--end');

            // Flip only when it helps. A menu too wide for either side keeps its
            // authored alignment rather than trading one overflow for a worse
            // one — the old unguarded version would right-align a menu wider
            // than the band and push it further out than it started. The room
            // comparison ignores menu width, so the decision is also stable
            // across re-places while the trigger hasn't moved.
            if (isEnd && rect.left < effectiveLeft && roomRight > roomLeft) {
                menu.style.left = '0';
                menu.style.right = 'auto';
                menu.setAttribute('data-dropdown-xflip', '');
            } else if (!isEnd && rect.right > effectiveRight && roomLeft > roomRight) {
                menu.style.right = '0';
                menu.style.left = 'auto';
                menu.setAttribute('data-dropdown-xflip', '');
            }
        }
    }

    // ── Lazy content ──────────────────────────────────────────────────────────
    // A lazily-loaded menu opens at spinner/skeleton size, so the placement
    // decided at click time is based on a box that isn't the real one. Both
    // paths guard against it with the same mechanism: watch the open menu and
    // re-place once the content is actually there.
    //
    // Framework-agnostic by construction — any DOM change inside the menu
    // counts, so htmx, fetch + innerHTML and hand-rolled lazy menus all benefit
    // without the JS knowing which one it is. (htmx:afterSettle is still wired
    // up below for portals; the two are idempotent and agree, so whichever
    // fires first wins and the other is a no-op.)
    //
    // One observer suffices: closeAll runs first, so the only menus left open
    // are the new one and any menu it is nested inside. A nested menu takes the
    // observer over from its parent, whose content has already loaded.
    var menuObserver = null;
    var menuWatched = null;

    function unwatchMenu() {
        if (menuObserver) {
            menuObserver.disconnect();
            menuObserver = null;
        }
        menuWatched = null;
    }

    function watchMenu(menu, replace) {
        unwatchMenu();
        if (typeof MutationObserver !== 'function') return;

        var pending = false;
        menuObserver = new MutationObserver(function () {
            if (pending) return;
            pending = true;
            // Coalesce a burst of mutations into one re-place, and measure after
            // the browser has laid the new content out rather than mid-swap.
            requestAnimationFrame(function () {
                pending = false;
                if (menuWatched !== menu) return;
                if (!menu.classList.contains('show')) {
                    // Closed by code that bypassed closeMenu().
                    unwatchMenu();
                    return;
                }
                // A swapped-in form is pinned again, and a header a lazy load
                // wiped is injected again, before the sheet is placed.
                // Only while the menu is a phone sheet: on desktop the form
                // keeps its own button (see unpinSheetForm).
                var info = menu._dropdownOwner && portaledMenus.get(menu._dropdownOwner);
                if (info && info.sheet && menu.hasAttribute('data-sheet-ready')) prepareSheet(info.trigger, menu);
                if (replace) replace();
            });
        });

        // childList/characterData only, never attributes: the re-place writes
        // class and style on the menu, and observing attributes would make it
        // retrigger itself.
        menuObserver.observe(menu, { childList: true, subtree: true, characterData: true });
        menuWatched = menu;
    }

    function watchInPlace(menu, trigger) {
        watchMenu(menu, function () {
            if (!document.contains(trigger)) {
                // Trigger was swapped away underneath us.
                unwatchMenu();
                return;
            }
            placeInPlace(menu, trigger, false);
        });
    }

    function getSheetBackdrop() {
        if (!sheetBackdrop) {
            sheetBackdrop = document.createElement('div');
            sheetBackdrop.className = 'backdrop backdrop--faded';
            document.body.appendChild(sheetBackdrop);
            // Stop the tap here when it closed a menu. This element has the
            // .backdrop class, so modals.js's document handler would otherwise
            // also close the sheet the dropdown was opened from.
            sheetBackdrop.addEventListener('click', function (e) {
                if (closeAll()) e.stopPropagation();
            });
        }
        return sheetBackdrop;
    }

    function showSheetBackdrop() {
        var el = getSheetBackdrop();
        el.offsetHeight;
        el.classList.add('show');
    }

    function hideSheetBackdrop() {
        if (sheetBackdrop) {
            sheetBackdrop.classList.remove('show');
        }
    }

    // ── Keyboard tracking ─────────────────────────────────────────────────────
    // iOS Safari does not shrink the layout viewport for the keyboard; only
    // visualViewport shrinks. offset is the height the keyboard covers at the
    // bottom of the layout viewport, height is the visible band. Android
    // shrinks the layout viewport instead, so there offset is 0 and height is
    // innerHeight. Pure, so it can be tested with a stubbed viewport.
    function sheetKeyboardMetrics(vv, innerHeight) {
        if (!vv) return { offset: 0, height: innerHeight };
        return {
            offset: Math.max(0, innerHeight - (vv.offsetTop + vv.height)),
            height: Math.max(0, vv.height)
        };
    }

    function applySheetMetrics(menu) {
        var m = sheetKeyboardMetrics(window.visualViewport, window.innerHeight);
        menu.style.setProperty('--sheet-keyboard-offset', m.offset + 'px');
        menu.style.setProperty('--sheet-visible-height', m.height + 'px');
        // A picker scrolls as one piece with the injected title pinned on top;
        // CSS sticks the combobox search box this far down, right under it.
        // Measured here because the menu is open and laid out by now.
        var header = menu.classList.contains('dropdown-sheet--picker') &&
            menu.querySelector('.dropdown-sheet__header[data-sheet-injected]');
        if (header) menu.style.setProperty('--sheet-header-height', header.getBoundingClientRect().height + 'px');
    }

    function clearSheetMetrics(menu) {
        menu.style.removeProperty('--sheet-keyboard-offset');
        menu.style.removeProperty('--sheet-visible-height');
        menu.style.removeProperty('--sheet-header-height');
    }

    function showDropdownBackdrop(menu) {
        hideDropdownBackdrop();
        dropdownBackdrop = document.createElement('div');
        dropdownBackdrop.className = 'dropdown-backdrop';
        dropdownBackdrop.addEventListener('click', function () {
            closeAll();
        });

        // Insert before the menu's .dropdown wrapper so backdrop sits behind it
        var dropdown = menu.closest('.dropdown');
        var parent = dropdown ? dropdown.parentNode : document.body;
        if (dropdown) {
            parent.insertBefore(dropdownBackdrop, dropdown);
        } else {
            parent.appendChild(dropdownBackdrop);
        }

        dropdownBackdrop.offsetHeight; // reflow
        dropdownBackdrop.classList.add('show');
    }

    function hideDropdownBackdrop() {
        if (dropdownBackdrop) {
            dropdownBackdrop.classList.remove('show');
            if (dropdownBackdrop.parentNode) {
                dropdownBackdrop.parentNode.removeChild(dropdownBackdrop);
            }
            dropdownBackdrop = null;
        }
    }

    function resetPanels(menu) {
        var panels = menu.querySelectorAll('[data-menu-panel]');
        if (!panels.length) return;
        panels.forEach(function (p) {
            p.style.display = p.getAttribute('data-menu-panel') === 'main' ? '' : 'none';
        });
    }

    // Natural border-box height of the menu's in-flow content. Like scrollHeight,
    // but overflow from absolutely-positioned descendants is left out: a
    // typeahead's floating results panel hangs out of the menu and is sized to
    // the screen by typeahead.js, and counting it here capped a menu whose own
    // content fit, turning it into a scroll box that clipped the panel. (A
    // .dropdown-menu-popout/-panel is overflow-y: auto in CSS regardless, so a
    // floating panel inside one is clipped either way; this matters for bare
    // [data-dropdown-portal] and .data-table portaled menus.)
    // offsetTop is layout position, unaffected by the menu's current scrollTop.
    function menuContentHeight(menu) {
        var cs = getComputedStyle(menu);
        var bottom = 0;
        for (var child = menu.firstElementChild; child; child = child.nextElementSibling) {
            var ccs = getComputedStyle(child);
            if (ccs.position === 'absolute' || ccs.position === 'fixed' || ccs.display === 'none') continue;
            bottom = Math.max(bottom, child.offsetTop + child.offsetHeight + (parseFloat(ccs.marginBottom) || 0));
        }
        return bottom + (parseFloat(cs.paddingBottom) || 0) +
            (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
    }

    // A portaled menu is position:fixed, so scrolling the page never reaches a
    // part of it that hangs past the viewport — on a phone, a long checkbox list
    // in a .filter-bar chip lost its tail and its Apply button. When the whole
    // menu fits neither below nor above its trigger, open it on the roomier side
    // and cap its height to that room so it scrolls inside itself. Returns the
    // top coordinate, or null when the menu fits and the caller's placement stands.
    //
    // Measures the content without clearing the cap first: clearing it would
    // reset the menu's own scroll position on every scroll/resize re-place.
    function fitPortalHeight(portalInfo, triggerRect, limit, MARGIN) {
        var menu = portalInfo.menu;
        // The CSS cap (popouts carry one), read once before any inline cap hides it.
        if (!('cssMaxHeight' in portalInfo)) {
            portalInfo.cssMaxHeight = parseFloat(getComputedStyle(menu).maxHeight);
        }
        var marginTop = parseFloat(getComputedStyle(menu).marginTop) || 0;
        var height = menuContentHeight(menu);
        if (!isNaN(portalInfo.cssMaxHeight)) height = Math.min(height, portalInfo.cssMaxHeight);

        var below = limit - (triggerRect.bottom + 4) - marginTop;
        var above = triggerRect.top - 4 - MARGIN;
        if (height <= below || height <= above) {
            menu.style.maxHeight = '';
            menu.style.overflowY = '';
            return null;
        }

        var room = Math.max(above, below);
        menu.style.maxHeight = room + 'px';
        menu.style.overflowY = 'auto';
        return above > below
            ? triggerRect.top - 4 - room - marginTop
            : triggerRect.bottom + 4;
    }

    // `monotonic` is set by the content-settled path: direction may go down→up
    // but never up→down. Scroll/resize omit it and re-decide freely.
    function repositionPortal(portalInfo, monotonic) {
        var menu = portalInfo.menu;
        var trigger = portalInfo.trigger;

        if (!trigger || !menu) return;

        // Trigger has been detached from the DOM (e.g. its row was replaced by
        // a WebSocket-driven refresh). The menu has no positioning context now —
        // its rect would be (0,0,0,0), and the menu would reflow to top-left.
        // Tear it down instead.
        if (!document.contains(trigger)) {
            // Find the dropdown key for this menu and clean up via closeMenu
            portaledMenus.forEach(function (info, dropdownKey) {
                if (info.menu === menu) closeMenu(menu, dropdownKey);
            });
            return;
        }

        // A mobile sheet is pinned to the bottom edge by CSS — there's nothing to place.
        if (portalInfo.sheet) return;

        // Use getBoundingClientRect for sub-pixel accuracy (same as open path)
        var triggerRect = trigger.getBoundingClientRect();
        var menuRect = menu.getBoundingClientRect();
        var menuWidth = menuRect.width;
        var menuHeight = menuRect.height;

        var MARGIN = 8;
        var left = triggerRect.left;
        var bottomOffset = portalInfo.bottomOffset || 0;
        var limit = window.innerHeight - MARGIN - bottomOffset;
        var top;

        if (monotonic) {
            // An already-upward menu stays upward — content growth must not make
            // it flop back down. A downward one may still be rescued: a lazily
            // loaded menu is measured at spinner size on the open path, so its
            // original decision was made against a box that wasn't the real one.
            // Down→up only, so repeated swaps can't oscillate.
            var wasFlippedUp = menuRect.top < triggerRect.top;
            if (wasFlippedUp) {
                top = triggerRect.top - menuHeight - 4;
            } else {
                top = triggerRect.bottom + 4;
                if (top + menuHeight > limit) {
                    top = triggerRect.top - menuHeight - 4;
                }
            }
        } else {
            top = triggerRect.bottom + 4;
            if (top + menuHeight > limit) {
                top = triggerRect.top - menuHeight - 4;
            }
        }

        // A menu too tall for either side gets capped and scrolls instead.
        var fitTop = fitPortalHeight(portalInfo, triggerRect, limit, MARGIN);
        if (fitTop !== null) {
            top = fitTop;
        } else {
            // Keep the menu inside the visible band. Without the bottom clamp a
            // menu that grew after opening downward — and that has nowhere better
            // to go — just hangs off the bottom edge.
            //
            // style.top positions the MARGIN box, but menuHeight is the border box
            // from getBoundingClientRect, so the rendered bottom is
            // top + marginTop + menuHeight. .dropdown-menu carries margin-top: 4px
            // and .dropdown-menu-popout overrides it negative — subtract whichever
            // applies or the clamp lands the bottom edge past the limit.
            var marginTop = parseFloat(getComputedStyle(menu).marginTop) || 0;
            top = Math.min(top, limit - menuHeight - marginTop);
        }
        top = Math.max(MARGIN, top);

        if (left + menuWidth > window.innerWidth - MARGIN) {
            left = triggerRect.right - menuWidth;
        }
        left = Math.max(MARGIN, Math.min(left, window.innerWidth - menuWidth - MARGIN));

        menu.style.top = top + 'px';
        menu.style.left = left + 'px';
    }

    function repositionAllPortals() {
        portaledMenus.forEach(function (portalInfo) {
            repositionPortal(portalInfo);
        });
    }

    // Content landed in a portaled menu — re-place it monotonically and animate
    // the move so it doesn't snap. Shared by the MutationObserver and the
    // htmx:afterSettle listener; running it twice for one swap is harmless
    // because the second call computes the same coords.
    function settlePortal(portalInfo) {
        var menu = portalInfo.menu;
        menu.style.transition = 'top 180ms ease-out, left 180ms ease-out';
        repositionPortal(portalInfo, true);  // monotonic
        setTimeout(function () { menu.style.transition = ''; }, 220);
    }

    function trackPortal(dropdown, portalInfo) {
        portaledMenus.set(dropdown, portalInfo);
        portalInfo.menu._dropdownOwner = dropdown;
        dropdown._portaledMenu = portalInfo.menu;
    }

    function restorePortal(dropdown) {
        if (portaledMenus.has(dropdown)) {
            var portalInfo = portaledMenus.get(dropdown);
            var menu = portalInfo.menu;
            // A field left focused in a hidden sheet would keep the iOS keyboard
            // up. This lives here, not in closeMenu, because an item click
            // un-portals the sheet (capture phase) before closeMenu runs, and
            // every un-portal path must blur. Desktop keeps the browser's own
            // focus handling.
            if (portalInfo.sheet && menu.contains(document.activeElement)) document.activeElement.blur();
            var originalParent = portalInfo.originalParent;
            var originalNextSibling = portalInfo.originalNextSibling;

            // Reset all portal inline styles
            menu.style.position = '';
            menu.style.top = '';
            menu.style.left = '';
            menu.style.right = '';
            menu.style.bottom = '';
            menu.style.maxHeight = '';
            menu.style.overflowY = '';
            menu.style.width = '';
            menu.style.zIndex = '';
            menu.style.visibility = '';

            // .dropdown-sheet and --picker stay; the next open recomputes --picker.
            // A phone-prepared menu keeps its __body and --inline footer, which
            // the structured CSS renders correctly only under .dropdown-sheet.
            delete menu._dropdownOwner;
            delete dropdown._portaledMenu;

            // Move back to original position
            if (originalNextSibling && originalNextSibling.parentNode === originalParent) {
                originalParent.insertBefore(menu, originalNextSibling);
            } else {
                originalParent.appendChild(menu);
            }

            portaledMenus.delete(dropdown);
        }
    }

    // Lookups across the portal gap, shared with typeahead.js and combobox.js
    // through window._cc. A portaled menu is not a descendant of its .dropdown
    // wrapper, so closest() from inside the menu and querySelector() from the
    // wrapper both miss; _dropdownOwner / _portaledMenu bridge the two.
    function ownerOf(node, sel) {
        var found = node.closest(sel);
        if (found) return found;
        var menu = node.closest('.dropdown-menu');
        return menu && menu._dropdownOwner ? menu._dropdownOwner.closest(sel) : null;
    }

    function findIn(container, sel) {
        var found = container.querySelector(sel);
        if (!found && container._portaledMenu) found = container._portaledMenu.querySelector(sel);
        return found;
    }

    function findAllIn(container, sel) {
        var found = container.querySelectorAll(sel);
        if (!found.length && container._portaledMenu) found = container._portaledMenu.querySelectorAll(sel);
        return found;
    }

    function closeMenu(menu, dropdown) {
        // JS only removes state it owns: .show, .dropdown-menu--up, the
        // horizontal-correction marker and the keyboard-tracking properties.
        menu.classList.remove('show', 'dropdown-menu--up');
        menu.removeAttribute('data-dropdown-xflip');
        if (menu === menuWatched) unwatchMenu();
        clearSheetMetrics(menu);
        // One backdrop serves a stack of sheets; it goes with the last one.
        if (!document.querySelector('.dropdown-sheet.show')) hideSheetBackdrop();
        hideDropdownBackdrop();
        // Clear any inline styles set during open (overflow flip, scroll-container positioning)
        menu.style.position = '';
        menu.style.top = '';
        menu.style.left = '';
        menu.style.right = '';
        menu.style.bottom = '';
        menu.style.width = '';
        menu.style.maxHeight = '';
        menu.style.overflowY = '';
        resetPanels(menu);

        if (dropdown) {
            dropdown.classList.remove('show');
            restorePortal(dropdown);
        }
    }

    function closeAll(except, exceptDropdown) {
        var closed = false;
        document.querySelectorAll('.dropdown-menu.show').forEach(function (menu) {
            if (menu === except) return;
            // Keep a menu open when the one being toggled sits inside it — e.g. a
            // row's contact popout holding its own Call menu. Closing the parent
            // would restore its portal and strand the child at the page's top-left.
            // Test the child's .dropdown wrapper, not its menu: a portaled child menu
            // lives on <body>, but the wrapper never moves.
            if (exceptDropdown && menu.contains(exceptDropdown)) return;

            var dropdown = menu.closest('.dropdown');
            if (!dropdown) {
                // Menu may be portaled — find the dropdown that owns it
                portaledMenus.forEach(function (portalInfo, dropdownKey) {
                    if (portalInfo.menu === menu) {
                        dropdown = dropdownKey;
                    }
                });
            }

            // Only close menus owned by SeeMe (have a [data-dropdown] trigger or are portaled)
            if (dropdown && !dropdown.querySelector('[data-dropdown]') && !portaledMenus.has(dropdown)) return;

            closeMenu(menu, dropdown);
            closed = true;
        });
        return closed;
    }

    // A header the markup brought, as opposed to the title prepareSheet injects.
    var AUTHORED_HEADER = '.dropdown-sheet__header:not([data-sheet-injected])';

    // Gives a menu its phone-sheet shape. Runs only when the menu is about to
    // open as a sheet. The menu keeps .dropdown-sheet, .dropdown-sheet--picker,
    // data-sheet-ready and the hidden --sheet-only header after that; only the
    // pinned form part is undone on a later desktop open.
    //
    // A menu that holds a typeahead or combobox is a picker: full visible
    // height, the title pinned on top with the search box under it. The picker
    // class is decided on every open, because lazily loaded content can make a
    // menu a picker later. Every menu, picker or not, gets a --sheet-only
    // title (hidden on desktop by CSS) once, unless sheetTitle returns '' or the
    // menu already has a hand-authored .dropdown-sheet__header. The title is the first of:
    //   1. data-sheet-title on the trigger;
    //   2. the label of the trigger's .form-group (a direct-child <label>,
    //      .form-group__label or .form-label, any element);
    //   3. a label[for] pointing at the trigger's id;
    //   4. the <dt> right before the <dd> holding the trigger (a detail list);
    //   5. the column header of the table cell holding the trigger: the cell
    //      at the same index in the thead's first row, skipped when either
    //      row has a colspan;
    //   6. the label of a .dropdown-form menu holding a single field (its
    //      only .dropdown-form__label), which the sheet then hides as a
    //      duplicate of the title; a form with several labels names one field,
    //      not the menu, so it gives no title;
    //   7. for a .chip or button trigger only (not a .combobox-trigger): its
    //      aria-label cut at the first ":" or "(" ("Seller possession: At
    //      Close" → "Seller possession");
    //   8. a .chip trigger's .chip-label-dim, trailing ":" dropped
    //      ("Assignee:" → "Assignee");
    //   9. the same chip or button's own text cut at the first ":" or "("
    //      ("Status: Active" → "Status", "Pool (2)" → "Pool"), unless the
    //      chip shows only a value (.chip-label with no .chip-label-dim).
    // Any other trigger (a table cell, a list row, a status pill, a combobox
    // showing its value or placeholder) gives no title from its text, since
    // that text is the field's value. With none of those the sheet has no
    // header. A trigger whose text is the current value but still wants a
    // title sets data-sheet-title or an aria-label.
    //
    // A menu that already carries its own .dropdown-sheet__header is left
    // alone and never becomes a picker; the hand-built shape wins.
    function sheetTitle(trigger, menu) {
        var title = trigger.getAttribute('data-sheet-title');
        if (title) return title;
        var group = trigger.closest('.form-group');
        var labels = group ? group.querySelectorAll(':scope > label, :scope > .form-group__label, :scope > .form-label') : [];
        for (var i = 0; i < labels.length; i++) {
            if (!menu.contains(labels[i])) return labels[i].textContent;
        }
        var forLabel = trigger.id && document.querySelector('label[for="' + CSS.escape(trigger.id) + '"]');
        if (forLabel) return forLabel.textContent;
        var dd = trigger.closest('dd');
        var dt = dd && dd.previousElementSibling;
        if (dt && dt.tagName === 'DT' && dt.textContent.trim()) return dt.textContent;
        var head = columnHeader(trigger);
        if (head) return head;
        var formLabels = menu.classList.contains('dropdown-form') ? menu.querySelectorAll('.dropdown-form__label') : [];
        if (formLabels.length === 1 && formLabels[0].textContent.trim()) return formLabels[0].textContent;
        if (!trigger.matches('.chip, .btn, [class*="btn-"]') || trigger.matches('.combobox-trigger')) return '';
        var aria = trigger.getAttribute('aria-label');
        if (aria) return aria.split(/[:(]/)[0];
        var dim = trigger.matches('.chip') && trigger.querySelector('.chip-label-dim');
        if (dim) return dim.textContent.replace(/:\s*$/, '');
        if (trigger.matches('.chip') && trigger.querySelector('.chip-label')) return '';
        return trigger.textContent.split(/[:(]/)[0];
    }

    // The thead text over the table cell holding the trigger, or ''.
    function columnHeader(trigger) {
        var cell = trigger.closest('td, th');
        var table = cell && cell.closest('table');
        var row = table && table.tHead && table.tHead.rows[0];
        if (!row) return '';
        // A colspan in either row throws the column indexes off.
        var spanned = function (c) { return c.colSpan !== 1; };
        var heads = row.cells;
        if (Array.prototype.some.call(heads, spanned) || Array.prototype.some.call(cell.parentElement.cells, spanned)) return '';
        var index = Array.prototype.indexOf.call(cell.parentElement.children, cell);
        var head = heads[index];
        return head ? head.textContent.trim() : '';
    }

    function prepareSheet(trigger, menu) {
        if (menu.querySelector(AUTHORED_HEADER)) return;
        var picker = !!menu.querySelector('.typeahead-input, [data-combobox-search], .typeahead-results');
        menu.classList.toggle('dropdown-sheet--picker', picker);
        prepareSheetForm(menu);

        // data-sheet-ready marks a menu JS prepared (the observer and
        // unpinSheetForm read it). The form part re-runs on later sheet opens
        // and from the menu observer while the menu is a sheet; a desktop open
        // unpins it (unpinSheetForm). The header is injected whenever the menu
        // lacks one, so a lazy load that replaces the menu's children gets it
        // back.
        menu.setAttribute('data-sheet-ready', '');
        if (menu.querySelector('.dropdown-sheet__header[data-sheet-injected]')) return;

        var title = sheetTitle(trigger, menu).replace(/\s+/g, ' ').trim();
        if (!title) return;

        var header = document.createElement(menu.tagName === 'UL' ? 'li' : 'div');
        header.className = 'dropdown-sheet__header dropdown-sheet__header--sheet-only';
        header.setAttribute('data-sheet-injected', '');
        var heading = document.createElement('h4');
        heading.textContent = title;
        header.appendChild(heading);
        menu.insertBefore(header, menu.firstChild);

        // A form label that just repeats the title would show twice in the
        // sheet; CSS hides it there (it stays as the desktop label). The form
        // can be the menu itself or one inside a [data-menu-panel].
        var dup = menu.querySelector('.dropdown-form__label');
        if (dup && dup.textContent.trim().toLowerCase() === title.toLowerCase()) {
            dup.setAttribute('data-sheet-title-dup', '');
        }
    }

    // The form part of prepareSheet. A .dropdown-form menu's direct <form>
    // becomes the __body so it keeps its gutter once the menu padding is
    // zeroed, and its trailing action is pinned. A form that already has
    // __body is done. A new one (htmx replaced the form) loses the old pinned
    // footer first, which holds the old form's button.
    // A .dropdown-form menu holds its form as a direct child. A panel menu
    // nests one form per [data-menu-panel]; each panel pins its own trailing
    // action inside the panel, so a panel switch swaps the footer too.
    function sheetForms(menu) {
        if (menu.classList.contains('dropdown-form')) {
            var form = menu.querySelector(':scope > form');
            return form ? [form] : [];
        }
        return Array.prototype.slice.call(menu.querySelectorAll(':scope > [data-menu-panel] > form.dropdown-form'));
    }

    function prepareSheetForm(menu) {
        sheetForms(menu).forEach(function (form) {
            // A pinned form has no trailing action left; one that shows up again
            // (an innerHTML swap kept the form and replaced its children) is
            // pinned again in place of the stale footer.
            if (form.classList.contains('dropdown-sheet__body') && !trailingAction(form)) return;
            var stale = form.parentNode.querySelector(':scope > .dropdown-sheet__footer--inline');
            if (stale) stale.remove();
            form.classList.add('dropdown-sheet__body');
            pinTrailingAction(form);
        });
    }

    // The reverse of prepareSheetForm, for a desktop open: the pinned action goes
    // back to the end of its form and the __body class comes off. A stale footer
    // (htmx replaced the form, which brought its own button) is just removed.
    function unpinSheetForm(menu) {
        sheetForms(menu).forEach(function (form) {
            var footer = form.parentNode.querySelector(':scope > .dropdown-sheet__footer--inline');
            if (footer) {
                if (!trailingAction(form)) {
                    while (footer.firstChild) form.appendChild(footer.firstChild);
                }
                footer.remove();
            }
            form.classList.remove('dropdown-sheet__body');
        });
    }

    var sheetFormSeq = 0;
    function formId(form) {
        if (!form.id) form.id = 'sheet-form-' + (++sheetFormSeq);
        return form.id;
    }

    // A checkbox form's Apply button (or a Cancel/Save .btn-group) sits last in
    // the form, so in a long sheet it scrolls out of reach. Move it into a
    // __footer so it stays pinned; the button keeps submitting the form through
    // the `form` attribute. --inline marks a footer JS made; it is made on a
    // phone open and removed on the next desktop open (unpinSheetForm), so it
    // is never shown on desktop.
    // The form's last element when it is a submit button or a .btn-group.
    function trailingAction(form) {
        var last = form.lastElementChild;
        if (!last) return null;
        var isAction = last.matches('button[type="submit"], input[type="submit"]') || last.classList.contains('btn-group');
        return isAction ? last : null;
    }

    function pinTrailingAction(form) {
        var last = trailingAction(form);
        if (!last) return;
        var isGroup = last.classList.contains('btn-group');

        var id = formId(form);
        var buttons = isGroup ? last.querySelectorAll('button, input') : [last];
        for (var i = 0; i < buttons.length; i++) {
            if (!buttons[i].hasAttribute('form')) buttons[i].setAttribute('form', id);
        }

        var footer = document.createElement('div');
        footer.className = 'dropdown-sheet__footer dropdown-sheet__footer--inline';
        footer.appendChild(last);
        form.parentNode.appendChild(footer);
    }

    // A sheet is portaled to body, which takes its fields out of any <form>
    // the menu sat in. Point each such field back at its form through the
    // `form` attribute, before the move. The attribute stays after close.
    function linkOuterForm(menu) {
        menu.querySelectorAll('input, select, textarea, button').forEach(function (field) {
            var form = field.form;
            if (form && !field.hasAttribute('form') && !menu.contains(form)) {
                field.setAttribute('form', formId(form));
            }
        });
    }

    // Toggle on trigger click
    document.addEventListener('click', function (e) {
        var trigger = e.target.closest('[data-dropdown]');
        if (!trigger) return;

        e.preventDefault();
        e.stopPropagation();

        var dropdown = trigger.closest('.dropdown');
        if (!dropdown) return;

        // Check if menu is portaled first, otherwise look in dropdown
        var menu = dropdown.querySelector('.dropdown-menu');
        if (!menu && portaledMenus.has(dropdown)) {
            menu = portaledMenus.get(dropdown).menu;
        }
        if (!menu) return;

        // A menu closed by code that bypassed closeMenu() is still on body.
        // Restore it first, so every path below reads its real origin.
        if (!menu.classList.contains('show') && portaledMenus.has(dropdown)) restorePortal(dropdown);

        // Close other open menus, then toggle this one
        closeAll(menu, dropdown);
        var isOpening = !menu.classList.contains('show');
        var isPopout = menu.classList.contains('dropdown-menu-popout') || menu.classList.contains('dropdown-menu-panel') || trigger.hasAttribute('data-dropdown-portal');
        // Below BP_SM every menu is a sheet except detail popouts and triggers
        // marked data-no-sheet. The sheet test runs before isPopout, so a bare
        // data-dropdown-portal menu is a sheet on a phone.
        var isSheet = window.innerWidth < BP_SM && !trigger.hasAttribute('data-no-sheet') &&
            !menu.classList.contains('dropdown-menu-panel') && !menu.classList.contains('dropdown-menu-popout');

        // The pinned footer belongs to the phone sheet. A desktop open of a menu
        // that was a sheet earlier (resize, or a form swapped while closed) puts
        // the form back the way it was authored; prepareSheet pins it again on
        // the next phone open.
        if (isOpening && !isSheet && menu.hasAttribute('data-sheet-ready')) unpinSheetForm(menu);

        if (isOpening) {
            resetPanels(menu);
            // Reset both previous flips — a menu closed by code that bypassed
            // closeMenu() would otherwise reopen still pinned to its old side.
            menu.classList.remove('dropdown-menu--up');
            menu.removeAttribute('data-dropdown-xflip');
            // The app-shell record panel (.shell__panel) wraps a plain .sheet but sits
            // beside the page, not over it, so its menus get no dimming backdrop.
            if (trigger.closest('.sheet, .modal') && !trigger.closest('.shell__panel')) showDropdownBackdrop(menu);

            // Sheet portal mode: move to body to escape parent stacking context.
            // CSS handles all positioning (position:fixed, pinned to the bottom
            // edge) — JS only supplies the keyboard-tracking properties.
            if (isSheet) {
                menu.classList.add('dropdown-sheet');
                prepareSheet(trigger, menu);
                linkOuterForm(menu);

                var originalParent = menu.parentNode;
                var originalNextSibling = menu.nextSibling;

                document.body.appendChild(menu);
                menu.classList.add('show');

                trackPortal(dropdown, {
                    menu: menu,
                    trigger: trigger,
                    originalParent: originalParent,
                    originalNextSibling: originalNextSibling,
                    bottomOffset: getBottomOffset(trigger),
                    sheet: true
                });

                dropdown.classList.add('show');
                showSheetBackdrop();
                applySheetMetrics(menu);
                // An htmx re-render can replace the form while the sheet is open.
                watchMenu(menu);

            // Portal mode: for popout menus, move to body to escape overflow:hidden
            } else if (isPopout) {
                var originalParent = menu.parentNode;
                var originalNextSibling = menu.nextSibling;

                // ── Floating-UI-inspired measurement pattern ──────────────────────────
                // 1. Portal to body at (0,0) with visibility:hidden — element is laid
                //    out correctly but invisible, so getBoundingClientRect is accurate.
                //    right:auto / bottom:auto prevent CSS rules like .dropdown-menu-end
                //    { right:0 } from stretching the element while width is still auto.
                // 2. Measure via getBoundingClientRect (sub-pixel, no rounding artefacts).
                // 3. Lock width, compute final coords, apply them, then reveal.
                //    The user sees the menu appear directly at its final position — no flash.
                menu.style.position = 'fixed';
                menu.style.zIndex = '10000';
                menu.style.visibility = 'hidden';
                menu.style.top = '0';
                menu.style.left = '0';
                menu.style.right = 'auto';
                menu.style.bottom = 'auto';
                menu.style.width = '';           // let content drive width for measurement

                document.body.appendChild(menu);
                menu.classList.add('show');

                var portalInfo = {
                    menu: menu,
                    trigger: trigger,
                    originalParent: originalParent,
                    originalNextSibling: originalNextSibling,
                    bottomOffset: getBottomOffset(trigger)
                };
                trackPortal(dropdown, portalInfo);

                var triggerRect = trigger.getBoundingClientRect();
                var menuRect = menu.getBoundingClientRect();
                var menuWidth = menuRect.width;
                var menuHeight = menuRect.height;

                // Lock width so content doesn't reflow during repositions
                var hasWidthClass = /dropdown-menu--(sm|md|lg)/.test(menu.className);
                if (!hasWidthClass) menu.style.width = menuWidth + 'px';

                var MARGIN = 8;
                var top = triggerRect.bottom + 4;
                var left = triggerRect.left;

                // Flip decision uses a conservative buffered height so lazy-loaded
                // popouts (which open at spinner size, then grow) flip up early
                // enough to avoid a jarring reposition after content settles.
                //
                // Cap the guess at whatever actually bounds the menu, read from
                // the computed style rather than hardcoded: .dropdown-menu-popout
                // and -panel cap themselves at min(360px, 60vh), but a bare
                // data-dropdown-portal menu has no max-height at all — assuming
                // 360 there promises a ceiling the CSS does not provide, and the
                // menu opens down and overflows. The settled re-position below is
                // the backstop when the guess is still short.
                var heightForFlip = menuHeight + 150;
                var cssMaxHeight = parseFloat(getComputedStyle(menu).maxHeight);
                if (!isNaN(cssMaxHeight)) heightForFlip = Math.min(cssMaxHeight, heightForFlip);

                // Flip up if not enough room below
                var bottomOffset = getBottomOffset(trigger);
                if (top + heightForFlip > window.innerHeight - MARGIN - bottomOffset) {
                    top = triggerRect.top - menuHeight - 4;
                }
                var fitTop = fitPortalHeight(portalInfo, triggerRect, window.innerHeight - MARGIN - bottomOffset, MARGIN);
                if (fitTop !== null) top = fitTop;
                top = Math.max(MARGIN, top);

                // Right-align to trigger if left-align overflows viewport edge
                if (left + menuWidth > window.innerWidth - MARGIN) {
                    left = triggerRect.right - menuWidth;
                }
                left = Math.max(MARGIN, Math.min(left, window.innerWidth - menuWidth - MARGIN));

                // Apply final coords then reveal — menu appears at correct position, no flash
                menu.style.top = top + 'px';
                menu.style.left = left + 'px';
                menu.style.visibility = '';

                dropdown.classList.add('show');
                watchMenu(menu, function () { settlePortal(portalInfo); });

            } else {
                // Detect scroll container first — if inside one, portal to body
                // so the menu escapes any clipping ancestor. In-place position:fixed
                // is insufficient on iOS Safari, where -webkit-overflow-scrolling
                // and (historically) overflow:auto can make the scroll container
                // act as the containing block for fixed descendants.
                var scrollParent = trigger.closest('.data-table--scroll') ||
                                   trigger.closest('.data-table');

                // .filter-bar becomes overflow-x:auto on mobile — only treat as scroll
                // parent when it actually clips (skip --wrap variant and desktop)
                if (!scrollParent) {
                    var filterBar = trigger.closest('.filter-bar');
                    if (filterBar && getComputedStyle(filterBar).overflowX !== 'visible') {
                        scrollParent = filterBar;
                    }
                }

                if (scrollParent) {
                    // Portal to body using the same measurement pattern as popouts:
                    // append invisible at (0,0), measure, place, then reveal — no flash.
                    var originalParent = menu.parentNode;
                    var originalNextSibling = menu.nextSibling;

                    menu.style.position = 'fixed';
                    menu.style.zIndex = '10000';
                    menu.style.visibility = 'hidden';
                    menu.style.top = '0';
                    menu.style.left = '0';
                    menu.style.right = 'auto';
                    menu.style.bottom = 'auto';
                    menu.style.width = '';

                    document.body.appendChild(menu);
                    menu.classList.add('show');

                    var portalInfo = {
                        menu: menu,
                        trigger: trigger,
                        originalParent: originalParent,
                        originalNextSibling: originalNextSibling,
                        bottomOffset: getBottomOffset(trigger)
                    };
                    trackPortal(dropdown, portalInfo);

                    var triggerRect = trigger.getBoundingClientRect();
                    var menuRect = menu.getBoundingClientRect();
                    var menuWidth = menuRect.width;
                    var menuHeight = menuRect.height;

                    var hasWidthClass = /dropdown-menu--(sm|md|lg)/.test(menu.className);
                    if (!hasWidthClass) menu.style.width = menuWidth + 'px';

                    var MARGIN = 8;
                    var top = triggerRect.bottom + 4;
                    var left = triggerRect.left;

                    var bottomOffset = getBottomOffset(trigger);
                    if (top + menuHeight > window.innerHeight - MARGIN - bottomOffset) {
                        top = triggerRect.top - menuHeight - 4;
                    }
                    var fitTop = fitPortalHeight(portalInfo, triggerRect, window.innerHeight - MARGIN - bottomOffset, MARGIN);
                    if (fitTop !== null) top = fitTop;
                    top = Math.max(MARGIN, top);

                    if (left + menuWidth > window.innerWidth - MARGIN) {
                        left = triggerRect.right - menuWidth;
                    }
                    left = Math.max(MARGIN, Math.min(left, window.innerWidth - menuWidth - MARGIN));

                    menu.style.top = top + 'px';
                    menu.style.left = left + 'px';
                    menu.style.visibility = '';

                    dropdown.classList.add('show');
                    watchMenu(menu, function () { settlePortal(portalInfo); });
                } else {
                    menu.classList.add('show');
                    dropdown.classList.add('show');

                    placeInPlace(menu, trigger, true);
                    watchInPlace(menu, trigger);
                }
            }

            // Announce open so other modules can react (e.g. auto-resize.js
            // re-measures [data-autoresize] textareas now that the menu is visible —
            // critical for portaled menus where the menu is no longer a child of
            // the .dropdown wrapper, so click-based DOM lookups can't find it).
            trigger.dispatchEvent(new CustomEvent('dropdown:open', {
                bubbles: true,
                detail: { menu: menu, trigger: trigger, sheet: isSheet }
            }));

            // Auto-focus first input in dropdown-form when opened.
            // Form-style dropdowns are expected to receive typing immediately
            // (mobile search, inline edits, etc.) — saves the user a second tap.
            // A sheet focuses synchronously: iOS raises the keyboard only for a
            // focus inside the tap. typeahead.js and combobox.js focus the search
            // input of a .dropdown.typeahead / [data-combobox] wrapper from
            // dropdown:open, so a sheet leaves that one alone.
            if (isSheet) {
                // The form is the menu itself or sits inside a [data-menu-panel];
                // the first field in a displayed panel wins.
                var field = Array.prototype.find.call(
                    menu.querySelectorAll('.dropdown-form input:not([type="hidden"]):not([type="submit"]), .dropdown-form textarea, .dropdown-form select'),
                    function (el) { return el.offsetParent !== null; }
                );
                var ownsFocus = dropdown.classList.contains('typeahead') || dropdown.hasAttribute('data-combobox');
                if (field && !(ownsFocus && field.matches('.typeahead-input, [data-combobox-search]'))) field.focus({ preventScroll: true });
            } else if (menu.classList.contains('dropdown-form')) {
                setTimeout(function () {
                    var input = menu.querySelector('input:not([type="hidden"]):not([type="submit"]), textarea, select');
                    if (input) input.focus();
                }, 0);
            }
        } else {
            closeMenu(menu, dropdown);
        }
    });

    function findPortalInfo(menu) {
        var found = null;
        portaledMenus.forEach(function (info) {
            if (info.menu === menu) found = info;
        });
        return found;
    }

    // Panel switching: [data-show-panel="name"] shows that panel, hides others
    document.addEventListener('click', function (e) {
        var trigger = e.target.closest('[data-show-panel]');
        if (!trigger) return;
        e.preventDefault();
        var menu = trigger.closest('.dropdown-menu');
        if (!menu) return;
        var target = trigger.getAttribute('data-show-panel');
        menu.querySelectorAll('[data-menu-panel]').forEach(function (p) {
            p.style.display = p.getAttribute('data-menu-panel') === target ? '' : 'none';
        });
        // Clear locked width so menu can resize to new panel content
        menu.style.width = '';

        // Re-place for the new panel. The MutationObserver can't do it: it
        // watches childList/characterData, and this is a display toggle. A
        // portaled menu is position:fixed, so a taller panel that runs past the
        // fold is unreachable until fitPortalHeight caps it — the "Add new"
        // form's submit button sat below a 600px-tall window with nothing to
        // scroll. Drop any cap fitted to the old panel first, or a short panel
        // would keep the tall one's height and side. A switch is navigation,
        // not a swap, so re-decide freely rather than monotonically.
        var portalInfo = findPortalInfo(menu);
        if (portalInfo) {
            menu.style.maxHeight = '';
            menu.style.overflowY = '';
            repositionPortal(portalInfo);
        } else {
            var dropdown = menu.closest('.dropdown');
            var dropdownTrigger = dropdown && dropdown.querySelector('[data-dropdown]');
            if (dropdownTrigger) placeInPlace(menu, dropdownTrigger, true);
        }
    });

    // Portal restore for relative-target frameworks (htmx closest/find, etc.)
    // ────────────────────────────────────────────────────────────────────────
    // A portaled menu lives on <body>, detached from its logical DOM position.
    // Frameworks that resolve a target relative to the clicked element (htmx's
    // `closest [data-task-id]`, `closest tr`, …) walk the item's ancestors —
    // which, while portaled, are <body>, not the original row — and fail.
    // Running in the CAPTURE phase, this fires before the item's own
    // target-phase listener (htmx attaches on the element), so we move the menu
    // back to its origin first; the resolver then sees the real ancestors.
    // Only done for clicks that will close the menu (the common case). Menus
    // that stay open (forms, keep-open, panel switches) are left portaled —
    // those don't use relative targets.
    document.addEventListener('click', function (e) {
        if (portaledMenus.size === 0) return;
        var item = e.target.closest('.dropdown-item');
        if (!item) return;
        var menu = item.closest('.dropdown-menu');
        if (!menu) return;
        var isForm = menu.classList.contains('dropdown-menu-form');
        // A [data-confirm-delete] item's FIRST click only arms the confirm — the menu
        // stays open, so un-portaling here would move it back inside a clipping scroll
        // container and drop it off-screen, making the confirm click unreachable.
        // confirm-delete.js registers after this file (seeme.js import order), so the
        // armed attribute isn't set yet on click one and is set on click two — where the
        // restore is correct, and must happen before htmx resolves the 'confirmed' target.
        var confirming = e.target.closest('[data-confirm-delete]');
        var arming = confirming && !confirming.hasAttribute('data-confirm-armed');
        if (isForm || arming || item.hasAttribute('data-dropdown-keep-open') || item.hasAttribute('data-show-panel')) return;
        portaledMenus.forEach(function (portalInfo, dropdown) {
            if (portalInfo.menu === menu) restorePortal(dropdown);
        });
    }, true);

    // Close when clicking a dropdown item (selection made)
    // Unless it has [data-dropdown-keep-open], [data-show-panel], or lives inside .dropdown-menu-form
    document.addEventListener('click', function (e) {
        var item = e.target.closest('.dropdown-item');
        if (!item) return;
        var menu = item.closest('.dropdown-menu');
        var isForm = menu && menu.classList.contains('dropdown-menu-form');
        if (!isForm && !item.hasAttribute('data-dropdown-keep-open') && !item.hasAttribute('data-show-panel')) {
            closeAll();
        }
    });

    // Explicit close trigger — use on any element inside a dropdown menu
    // (e.g. Cancel buttons in confirm dialogs) that isn't a .dropdown-item.
    document.addEventListener('click', function (e) {
        var closer = e.target.closest('[data-dropdown-close]');
        if (!closer) return;
        if (closer.classList.contains('dropdown-item')) return; // already handled above
        closeAll();
    });

    // Close on click outside
    // Check against the open menu and trigger specifically — NOT the whole .dropdown
    // container, which can be wider than the trigger and create a "dead zone" that
    // feels like a bug (clicking next to the trigger doesn't close the menu).
    document.addEventListener('click', function (e) {
        // Trigger clicks are handled by the toggle handler above
        if (e.target.closest('[data-dropdown]')) return;

        // Clicks inside an open menu stay open
        if (e.target.closest('.dropdown-menu.show')) return;

        // Portaled menus (popout) are outside the DOM tree — check separately
        var clickedPortaledMenu = false;
        portaledMenus.forEach(function (portalInfo) {
            if (portalInfo.menu.contains(e.target)) {
                clickedPortaledMenu = true;
            }
        });
        if (clickedPortaledMenu) return;

        closeAll();
    });

    // Close on Escape, and stop the key there when a menu closed so the sheet or
    // modal under it stays open. This works because dropdowns.js registers before
    // modals.js (and Houses sheets.js), whose Escape listeners are also on document.
    document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && closeAll()) {
            e.stopImmediatePropagation();
        }
    });

    // Reposition portaled menus on scroll or resize to keep them aligned with triggers
    window.addEventListener('scroll', function () {
        if (portaledMenus.size > 0) {
            repositionAllPortals();
        }
    }, true);

    // A resize across BP_SM (a rotation) closes everything: a sheet stranded
    // on body at desktop width, or a floating menu at phone width, is wrong.
    var lastWidth = window.innerWidth;
    window.addEventListener('resize', function () {
        var crossed = (lastWidth < BP_SM) !== (window.innerWidth < BP_SM);
        lastWidth = window.innerWidth;
        if (crossed) closeAll();
        if (portaledMenus.size > 0) {
            repositionAllPortals();
        }
    });

    // Keep open sheets above the keyboard. One listener pair for every sheet,
    // coalesced to one measurement per frame. visualViewport.scale is ignored.
    if (window.visualViewport) {
        var metricsPending = false;
        var onViewportChange = function () {
            if (metricsPending) return;
            metricsPending = true;
            requestAnimationFrame(function () {
                metricsPending = false;
                portaledMenus.forEach(function (portalInfo) {
                    if (portalInfo.sheet && portalInfo.menu.classList.contains('show')) applySheetMetrics(portalInfo.menu);
                });
            });
        };
        window.visualViewport.addEventListener('resize', onViewportChange);
        window.visualViewport.addEventListener('scroll', onViewportChange);
    }

    // HTMX cleanup: external code (inline hx-on handlers, data-success-close-dropdown)
    // may remove .show from a dropdown-menu without going through closeMenu(), leaving
    // the backdrops and the keyboard-tracking properties orphaned. After every HTMX
    // request, tear down whatever no open menu still needs.
    document.body.addEventListener('htmx:afterRequest', function () {
        if (dropdownBackdrop && !document.querySelector('.dropdown-menu.show')) {
            hideDropdownBackdrop();
        }
        if (!document.querySelector('.dropdown-sheet.show')) {
            hideSheetBackdrop();
            portaledMenus.forEach(function (portalInfo) {
                if (portalInfo.sheet) clearSheetMetrics(portalInfo.menu);
            });
        }
    });

    // hx-include="find …" on a .dropdown wrapper searches the wrapper's subtree, so it
    // misses a hidden input while the menu is on <body> (keep-open typeahead pickers
    // that post on typeahead:select). Resolve those selectors against the portaled menu
    // and add the values the request is missing. Reads hx-include on the requesting
    // element only; htmx's inherited hx-include from an ancestor is not covered.
    document.body.addEventListener('htmx:configRequest', function (e) {
        var elt = e.detail.elt;
        var dropdown = elt.closest ? elt.closest('.dropdown') : null;
        var menu = dropdown && dropdown._portaledMenu;
        var include = menu && elt.getAttribute('hx-include');
        if (!include) return;
        var missing = {};
        include.split(',').forEach(function (part) {
            part = part.trim();
            if (part.indexOf('find ') !== 0) return;
            menu.querySelectorAll(part.slice(5).trim()).forEach(function (field) {
                // Same rule htmx uses for an include: no disabled field, no unticked box.
                if (field.disabled || (/^(checkbox|radio)$/.test(field.type) && !field.checked)) return;
                if (!field.name || e.detail.parameters[field.name] !== undefined) return;
                (missing[field.name] = missing[field.name] || []).push(field.value);
            });
        });
        // A repeated name (a checkbox group) sends every value, as htmx would.
        Object.keys(missing).forEach(function (name) {
            e.detail.parameters[name] = missing[name].length === 1 ? missing[name][0] : missing[name];
        });
    });

    // HTMX swap cleanup: a portaled menu lives on <body>, so it survives a swap that
    // removes its origin .dropdown — leaving an open menu whose trigger is detached and
    // whose relative-target lookups (htmx `closest tr`, …) resolve into dead DOM, or into
    // nothing at all. repositionPortal() already tears down a detached trigger, but only
    // scroll/resize ever reach that guard. Run the same check the moment the origin goes.
    //
    // Ordinary clicks never get here — an outside click fires closeAll() before htmx runs,
    // and a plain .dropdown-item is covered by the capture-phase restore above. This is for
    // the swaps with no closing click in front of them: [data-dropdown-keep-open] and
    // .dropdown-menu-form items, and WS/SSE/polling/OOB swaps that land while a menu is open.
    document.body.addEventListener('htmx:afterSwap', function () {
        if (portaledMenus.size === 0) return;
        portaledMenus.forEach(function (portalInfo, dropdown) {
            if (document.contains(portalInfo.trigger)) return;
            // Don't yank a menu the user is actively typing in — a background poll
            // shouldn't destroy in-progress input. It'll be torn down by the normal
            // close paths (or repositionPortal's guard) as soon as focus moves.
            //
            // Scoped to real text entry, deliberately. Chromium focuses a clicked
            // button, so a blanket menu.contains(activeElement) check would skip the
            // teardown for the very swap that just orphaned the menu — it only appears
            // to work because loading.js disables btn-* triggers on htmx:beforeRequest,
            // which blurs them. [data-no-auto-loading] opts out of that and brings the
            // orphan straight back. A focused button that just fired the swap is not
            // in-progress typing, so don't treat it as such.
            //
            // A sheet is position: fixed, so it stays put without its trigger just
            // like a floating portaled menu, and the normal close paths tear it down.
            var ae = document.activeElement;
            if (ae && portalInfo.menu.contains(ae) && (ae.isContentEditable ||
                (ae.matches('input, textarea, select') &&
                 !ae.matches('input[type=checkbox], input[type=radio], input[type=button], input[type=submit], input[type=reset]')))) return;
            closeMenu(portalInfo.menu, dropdown);
        });
    });

    // Expose proper close API so other modules can request dropdowns be closed
    // (with portal restoration, backdrop teardown, etc.) — see htmx-actions.js
    // `data-success-close-dropdown`.
    window._cc = window._cc || {};
    window._cc.closeAllDropdowns = function () { closeAll(); };
    // Close one menu through closeMenu (portal restore, backdrop, keyboard
    // properties); a menu nested in a kept-open parent leaves the parent open.
    window._cc.closeDropdown = function (menu) {
        var dropdown = menu._dropdownOwner || menu.closest('.dropdown');
        if (menu.classList.contains('show')) closeMenu(menu, dropdown);
    };
    window._cc.sheetKeyboardMetrics = sheetKeyboardMetrics;
    window._cc.ownerOf = ownerOf;
    window._cc.findIn = findIn;
    window._cc.findAllIn = findAllIn;
    // True while the menu is open as a phone sheet (its portal entry carries
    // sheet: true). The .dropdown-sheet class alone is not enough: it stays on
    // the menu after close, and hand-authored menus carry it at every width.
    window._cc.isSheet = function (menu) {
        var info = menu._dropdownOwner && portaledMenus.get(menu._dropdownOwner);
        return !!(info && info.sheet);
    };

    // Lazy-loaded popout content: reposition any portaled menu after HTMX content
    // settles. The open-path measures menu height while it shows only a spinner
    // placeholder, so the flip-up decision is based on that initial size. Once the
    // real content swaps in the menu grows, and without this re-measurement the
    // bottom can overflow the viewport.
    //
    // The MutationObserver above covers this too, and covers non-htmx loaders
    // besides. This listener stays because it fires after settle rather than
    // after mutation, so htmx-driven content that is still animating in gets its
    // final measurement here.
    document.body.addEventListener('htmx:afterSettle', function (evt) {
        if (portaledMenus.size === 0) return;
        var target = evt.target;
        portaledMenus.forEach(function (portalInfo) {
            var menu = portalInfo.menu;
            if (menu === target || menu.contains(target)) settlePortal(portalInfo);
        });
    });
})();
